import { traceChartNavigation } from "./chartNavigationTrace";

export const CHART_LAYOUTS = Object.freeze({
  SINGLE: "single",
  TWO_COLUMNS: "two-columns",
  TWO_ROWS: "two-rows",
  FOUR_GRID: "four-grid",
});

export const PANEL_IDS = Object.freeze({
  INDICATORS: "indicators",
  DRAW: "draw",
});

export const LINK_KEYS = Object.freeze({
  SYMBOL: "symbol",
  INTERVAL: "interval",
  RANGE: "range",
  CROSSHAIR: "crosshair",
  NAVIGATION: "navigation",
});

const DEFAULT_SYMBOL = "PLUG";
const DEFAULT_INTERVAL = "5m";
const DEFAULT_RANGE = "5D";
const DEFAULT_NAVIGATION = Object.freeze({
  viewportEnd: null,
  futureSpaceBars: 0,
});

const LAYOUT_PANE_COUNT = Object.freeze({
  [CHART_LAYOUTS.SINGLE]: 1,
  [CHART_LAYOUTS.TWO_COLUMNS]: 2,
  [CHART_LAYOUTS.TWO_ROWS]: 2,
  [CHART_LAYOUTS.FOUR_GRID]: 4,
});

function freshNavigation() {
  return { ...DEFAULT_NAVIGATION };
}

function createPane(index, overrides = {}) {
  return {
    id: `chart-${index + 1}`,
    symbol: DEFAULT_SYMBOL,
    interval: DEFAULT_INTERVAL,
    range: DEFAULT_RANGE,
    indicators: ["vwap", "ema20"],
    drawings: [],
    navigation: freshNavigation(),
    ...overrides,
  };
}

function defaultPanels() {
  return {
    [PANEL_IDS.INDICATORS]: {
      id: PANEL_IDS.INDICATORS,
      title: "Indicators",
      isOpen: false,
      isPinned: false,
      position: { x: 24, y: 76 },
      zIndex: 10,
    },
    [PANEL_IDS.DRAW]: {
      id: PANEL_IDS.DRAW,
      title: "Draw Tools",
      isOpen: false,
      isPinned: false,
      position: { x: 96, y: 108 },
      zIndex: 11,
    },
  };
}

export function createInitialChartWorkspaceState({ symbol = DEFAULT_SYMBOL } = {}) {
  const initialState = {
    layout: CHART_LAYOUTS.SINGLE,
    panes: [
      createPane(0, { symbol }),
      createPane(1, { symbol }),
      createPane(2, { symbol }),
      createPane(3, { symbol }),
    ],
    activePaneId: "chart-1",
    focus: {
      paneId: null,
      previousLayout: null,
    },
    links: {
      [LINK_KEYS.SYMBOL]: false,
      [LINK_KEYS.INTERVAL]: false,
      [LINK_KEYS.RANGE]: false,
      [LINK_KEYS.CROSSHAIR]: false,
      [LINK_KEYS.NAVIGATION]: false,
    },
    panels: defaultPanels(),
    nextPanelZIndex: 12,
  };
  traceChartNavigation("chartWorkspaceModel.createInitialChartWorkspaceState", {
    symbol,
    reason: "workspace reducer initialization",
    panes: initialState.panes.map((pane) => ({
      id: pane.id,
      symbol: pane.symbol,
      navigation: pane.navigation,
    })),
  }, { stack: true });
  return initialState;
}

export function visiblePaneCount(layout) {
  return LAYOUT_PANE_COUNT[layout] || 1;
}

export function getVisiblePanes(state) {
  if (state.focus.paneId) {
    const focused = state.panes.find((pane) => pane.id === state.focus.paneId);
    return focused ? [focused] : state.panes.slice(0, 1);
  }
  return state.panes.slice(0, visiblePaneCount(state.layout));
}

function bringPanelForward(state, panelId) {
  const panel = state.panels[panelId];
  if (!panel) return state;

  return {
    ...state,
    panels: {
      ...state.panels,
      [panelId]: {
        ...panel,
        zIndex: state.nextPanelZIndex,
      },
    },
    nextPanelZIndex: state.nextPanelZIndex + 1,
  };
}

function updatePaneWithOptionalLinks(state, paneId, patch) {
  const sourcePane = state.panes.find((pane) => pane.id === paneId);
  if (!sourcePane) return state;

  const hasOwn = (key) => Object.prototype.hasOwnProperty.call(patch, key);
  const sourceContextChanged = hasOwn("symbol") || hasOwn("interval") || hasOwn("range");
  const sourcePatch =
    sourceContextChanged && !hasOwn("navigation")
      ? { ...patch, navigation: freshNavigation() }
      : patch;

  const linkedPatch = {};
  let linkedContextChanged = false;
  if (hasOwn("symbol") && state.links[LINK_KEYS.SYMBOL]) {
    linkedPatch.symbol = patch.symbol;
    linkedContextChanged = true;
  }
  if (hasOwn("interval") && state.links[LINK_KEYS.INTERVAL]) {
    linkedPatch.interval = patch.interval;
    linkedContextChanged = true;
  }
  if (hasOwn("range") && state.links[LINK_KEYS.RANGE]) {
    linkedPatch.range = patch.range;
    linkedContextChanged = true;
  }
  if (hasOwn("navigation") && state.links[LINK_KEYS.NAVIGATION]) {
    linkedPatch.navigation = patch.navigation;
  } else if (linkedContextChanged) {
    linkedPatch.navigation = freshNavigation();
  }

  return {
    ...state,
    panes: state.panes.map((pane) => {
      if (pane.id === paneId) return { ...pane, ...sourcePatch };
      if (Object.keys(linkedPatch).length > 0) return { ...pane, ...linkedPatch };
      return pane;
    }),
  };
}

export function chartWorkspaceReducer(state, action) {
  switch (action.type) {
    case "SET_LAYOUT": {
      if (!LAYOUT_PANE_COUNT[action.layout]) return state;
      return {
        ...state,
        layout: action.layout,
        focus: { paneId: null, previousLayout: null },
      };
    }

    case "SET_ACTIVE_PANE":
      return state.panes.some((pane) => pane.id === action.paneId)
        ? { ...state, activePaneId: action.paneId }
        : state;

    case "UPDATE_PANE":
      return updatePaneWithOptionalLinks(state, action.paneId, action.patch || {});

    case "SET_LINK":
      return Object.prototype.hasOwnProperty.call(state.links, action.key)
        ? {
            ...state,
            links: { ...state.links, [action.key]: Boolean(action.enabled) },
          }
        : state;

    case "OPEN_PANEL": {
      const panel = state.panels[action.panelId];
      if (!panel) return state;
      const opened = {
        ...state,
        panels: {
          ...state.panels,
          [action.panelId]: { ...panel, isOpen: true },
        },
      };
      return bringPanelForward(opened, action.panelId);
    }

    case "CLOSE_PANEL": {
      const panel = state.panels[action.panelId];
      if (!panel) return state;
      return {
        ...state,
        panels: {
          ...state.panels,
          [action.panelId]: { ...panel, isOpen: false },
        },
      };
    }

    case "TOGGLE_PANEL":
      return chartWorkspaceReducer(state, {
        type: state.panels[action.panelId]?.isOpen ? "CLOSE_PANEL" : "OPEN_PANEL",
        panelId: action.panelId,
      });

    case "TOGGLE_PANEL_PIN": {
      const panel = state.panels[action.panelId];
      if (!panel) return state;
      return {
        ...state,
        panels: {
          ...state.panels,
          [action.panelId]: { ...panel, isPinned: !panel.isPinned },
        },
      };
    }

    case "MOVE_PANEL": {
      const panel = state.panels[action.panelId];
      if (!panel || panel.isPinned) return state;
      const nextPosition = action.position || panel.position;
      return {
        ...state,
        panels: {
          ...state.panels,
          [action.panelId]: {
            ...panel,
            position: {
              x: Math.max(0, Number(nextPosition.x) || 0),
              y: Math.max(0, Number(nextPosition.y) || 0),
            },
          },
        },
      };
    }

    case "FOCUS_PANEL":
      return bringPanelForward(state, action.panelId);

    case "ENTER_PANE_FOCUS": {
      if (!state.panes.some((pane) => pane.id === action.paneId)) return state;
      return {
        ...state,
        activePaneId: action.paneId,
        focus: {
          paneId: action.paneId,
          previousLayout: state.focus.paneId ? state.focus.previousLayout : state.layout,
        },
      };
    }

    case "EXIT_PANE_FOCUS":
      return {
        ...state,
        layout: state.focus.previousLayout || state.layout,
        focus: { paneId: null, previousLayout: null },
      };

    default:
      return state;
  }
}
